<template>
    <b-container>
      <form @submit.prevent="saveForm">
        <h1>New Aspect</h1>
  
        <!-- Choose form from a dropdown -->
        <label class="mt-2" for="aspect_form_id">Choose Aspect Type:</label>
        <b-form-select v-model="aspect_form_id" :options="form_options" />
  
        <!-- Only render form once we've picked a form and loaded the data -->
        <div v-if="aspect_form_id && form" class="mt-3">
  
          <h2>Aspect Form</h2>
  
          <!-- Optional "Date New" field if the form.settings provides a label -->
          <div v-if="date_new">
            <label>{{ form.settings.date_new }}</label>
            <DatePicker v-model="aspect.date_new" date-id="date_new" />
          </div>
  
          <!-- Optional "Date Purchased" field -->
          <div v-if="date_purchased">
            <label>{{ form.settings.date_purchased }}</label>
            <DatePicker v-model="aspect.date_purchased" date-id="date_purchased" />
          </div>
  
          <!-- Loop through the dynamic attributes from the JSON -->
           <div v-if="form.attributes">
          <div 
            v-for="(attribute, i) in form.attributes"
            :key="attribute.id"
            class="mt-3"
          >
            <b-form-group
              :id="`input-group-${i}`"
              :label="attribute.label"
              :description="attribute.tooltip"
            >
              <!-- SELECT -->
              <b-form-select
                v-if="attribute.type === 'Select'"
                :style="attribute.class"
                v-model="aspect['attribute' + attribute.column]"
                :options="attribute.value"
                :name="attribute.attribute_id"
                :required="attribute.required === 1"
                :class="{ 'is-invalid': errorMessages['attribute' + attribute.column] }"
              />
  
              <!-- PHONE TEXT (special mask) -->
              <b-form-input
                v-else-if="attribute.type === 'Text' && attribute.label.toLowerCase().includes('phone')"
                :style="attribute.class"
                v-model="aspect['attribute' + attribute.column]"
                :name="attribute.attribute_id"
                v-mask="'(###) ###-####'"
                :required="attribute.required === 1"
                :maxlength="15"
                :minlength="15"
                :class="{ 'is-invalid': errorMessages['attribute' + attribute.column] }"
              />
  
              <!-- REGULAR TEXT -->
              <b-form-input
                v-else-if="attribute.type === 'Text'"
                :style="attribute.class"
                v-model="aspect['attribute' + attribute.column]"
                :name="attribute.attribute_id"
                :required="attribute.required === 1"
                :maxlength="attribute.maxlength"
                :minlength="attribute.minlength"
                :class="{ 'is-invalid': errorMessages['attribute' + attribute.column] }"
              />
  
              <!-- TEXTAREA -->
              <b-form-textarea
                v-else-if="attribute.type === 'Textarea'"
                :style="attribute.class"
                v-model="aspect['attribute' + attribute.column]"
                :name="attribute.attribute_id"
                :required="attribute.required === 1"
                :maxlength="attribute.maxlength"
                :minlength="attribute.minlength"
                :class="{ 'is-invalid': errorMessages['attribute' + attribute.column] }"
              />
  
              <!-- CHECKBOX -->
              <b-form-checkbox
                v-else-if="attribute.type === 'Checkbox'"
                :style="attribute.class"
                v-model="aspect['attribute' + attribute.column]"
                :name="attribute.attribute_id"
                :required="attribute.required === 1"
                :class="{ 'is-invalid': errorMessages['attribute' + attribute.column] }"
              />
  
              <!-- DATE FIELD -->
              <DatePicker
                v-else-if="attribute.type === 'Date'"
                v-model="aspect['attribute' + attribute.column]"
                :date-id="'attribute' + attribute.column"
                :class="{ 'is-invalid': errorMessages['attribute' + attribute.column] }"
              />
  
              <!-- If none of the above, fallback to hidden -->
              <input
                v-else
                type="hidden"
                :name="attribute.attribute_id"
                :value="aspect['attribute' + attribute.column]"
              />
  
              <!-- Show manual validation error if present -->
              <b-form-invalid-feedback
                v-if="errorMessages['attribute' + attribute.column]"
              >
                {{ errorMessages['attribute' + attribute.column] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
  </div>
          <!-- "Details" field if you have a static field outside attributes -->
          <b-form-group label="Details">
            <b-form-input
              v-model="aspect.description"
              :class="{ 'is-invalid': errorMessages.description }"
            />
            <b-form-invalid-feedback v-if="errorMessages.description">
              {{ errorMessages.description }}
            </b-form-invalid-feedback>
          </b-form-group>
  
          <!-- Additional toggles based on form.settings, e.g. color, star, status, flag -->
          <b-form-group v-if="form.settings.color === 1" label="Color">
            <b-form-select v-model="aspect.opt_color" :options="colorOptions" />
          </b-form-group>
  
          <b-form-group v-if="form.settings.star === 1" label="Star">
            <b-form-select v-model="aspect.opt_star" :options="starOptions" />
          </b-form-group>
  
          <b-form-group v-if="form.settings.status === 1" label="Status">
            <b-form-select v-model="aspect.opt_status" :options="statusOptions" />
          </b-form-group>
  
          <b-form-group v-if="form.settings.flag === 1" label="Flag">
            <b-form-select v-model="aspect.opt_flag" :options="flagOptions" />
          </b-form-group>
  
          <hr />
          <b-button type="submit" variant="primary" :disabled="isSubmitting">
            Save Aspect
          </b-button>
        </div>
      </form>
    </b-container>
  </template>
  
  <script>
  import FormDataService from "@/services/FormDataService.js";
  import AspectDataService from "@/services/AspectDataService.js";
  import DatePicker from '@/components/DatePicker.vue';
  
  export default {
    name: "AspectNew",
    components: {
      DatePicker
    },
    props: ["company"],
    data() {
      return {
        // The main data model
        aspect: {
          aspect_form_id: null,
          date_new: '',
          date_purchased: '',
          description: '',
          opt_color: '',
          opt_star: '',
          opt_status: '',
          opt_flag: '',
          ref_id: false,
        },

        aspect_form_id: null,           // chosen form ID from the dropdown
        form: { settings: {} },         // loaded from FormDataService
        forms: [],                      // full list of forms
        form_options: [],               // array of {text, value} for <b-form-select>
        isSubmitting: false,            // track if we are currently submitting
        
  
        // If you store these separately:
        date_new: '',
        date_purchased: '',
  
        // This object holds any error messages for each field
        errorMessages: {},
  
        // Extra select options
        colorOptions: [
          { text: 'None', value: '1' },
          { text: 'Red', value: '2' },
          { text: 'Blue', value: '3' },
          { text: 'Green', value: '4' },
          { text: 'Yellow', value: '5' },
        ],
        starOptions: [
          { text: 'No', value: '0' },
          { text: 'Yes', value: '1' }
        ],
        statusOptions: [
          { text: 'New', value: '1' },
          { text: 'Prospect', value: '2' },
          { text: 'Verified', value: '3' },
          { text: 'Active', value: '4' },
          { text: 'Deleted', value: '5' }
        ],
        flagOptions: [
          { text: 'No', value: '0' },
          { text: 'Yes', value: '1' }
        ],
      };
    },
    created() {

      // if the querystring has aspect_id and a UUID, then 
      // Populate the dropdown for picking the aspect form then set aspect_form_id to it
      if (this.$route.query.aspect_id) {
        this.aspect_form_id = this.$route.query.aspect_id
      }
      
      FormDataService.list().then(res => {
        const forms = res?.data || res;
        if (!forms || !Array.isArray(forms)) {
          console.error('Failed to load form types');
          return;
        }
        this.form_options = forms.map(element => ({
          text: element.name,
          value: element.id
        }));
        this.forms = forms;
      });
    },
    methods: {
      // If you want to build a "name" field from multiple attributes
      setName() {
        if (!this.form || !this.form.settings.searchfields) return;
        this.aspect.name = this.form.settings.searchfields
          .map(col => this.aspect[`attribute${col}`] || "")
          .join(" ");
      },
  
      // Manual validation logic:
      // 1) If required => must not be empty, always run other checks if not empty
      // 2) If not required => skip checks if empty, otherwise run them
      validateFields() {
        // Clear old errors
        this.errorMessages = {};
        let isValid = true;
  
        // Example static field check:
        // if (this.aspect.description.trim() === '') {
        //   this.$set(this.errorMessages, 'description', 'Description is required');
        //   isValid = false;
        // }
  
        if (this.form?.attributes) {
          this.form.attributes.forEach(attribute => {
            const attrKey = `attribute${attribute.column}`;
            if (!(attrKey in this.aspect)) {
              // Ensure field is reactive
              this.$set(this.aspect, attrKey, "");
            }
  
            const value = String(this.aspect[attrKey] || "").trim();
            const isRequired = (attribute.required === 1);
  
            if (isRequired) {
              // Must not be empty
              if (!value) {
                this.$set(this.errorMessages, attrKey, `${attribute.label} is required`);
                isValid = false;
                return;
              }
              // If not empty, we check all other rules
              this.runFieldChecks(attribute, value, attrKey, isValid);
            } else {
              // Not required => if empty, no checks
              if (!value) {
                return; // do nothing
              }
              // If user entered something => run checks
              this.runFieldChecks(attribute, value, attrKey, isValid);
            }
          });
        }
  
        return isValid;
      },
  
      // Helper to apply minLength, maxLength, email, digits, etc.
      runFieldChecks(attribute, value, attrKey) {
        // Min length
        if (attribute.minlength && value.length < Number(attribute.minlength)) {
          this.$set(
            this.errorMessages,
            attrKey,
            `${attribute.label} must be at least ${attribute.minlength} characters`
          );
          // isValid = false;
          return;
        }
  
        // Max length
        if (attribute.maxlength && value.length > Number(attribute.maxlength)) {
          this.$set(
            this.errorMessages,
            attrKey,
            `${attribute.label} cannot exceed ${attribute.maxlength} characters`
          );
          // isValid = false;
          return;
        }
  
        // Email check
        if (attribute.validate === 'email') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            this.$set(this.errorMessages, attrKey, `${attribute.label} must be a valid email`);
            // isValid = false;
            return;
          }
        }
  
        // Digits check
        if (attribute.validate === 'digits') {
          const digitRegex = /^\d+$/;
          if (!digitRegex.test(value)) {
            this.$set(this.errorMessages, attrKey, `${attribute.label} must be digits only`);
            // isValid = false;
            return;
          }
        }
      },
  
      async saveForm() {
        // 1) Validate everything
        const isValid = this.validateFields();
        if (!isValid) {
          // If invalid, we do not submit
          return;
        }

        // Ensure aspect_form_id is set
        if (!this.aspect.aspect_form_id) {
          this.aspect.aspect_form_id = this.aspect_form_id;
        }

        if (!this.aspect.aspect_form_id) {
          this.$emit("error", "Please select an aspect type");
          return;
        }

        // 2) Set the "name" if needed
        this.setName();
        this.isSubmitting = true;

        try {
          // 3) Submit via your service
          const res = await AspectDataService.create(this.aspect);
          console.log('Create response:', res);

          // Check for error response
          if (res && res.status && res.status >= 400) {
            this.$emit("error", res.message || "Failed to create aspect");
            return;
          }

          // Handle different response formats
          const data = res?.data || res;
          if (Array.isArray(data)) {
            const idItem = data.find(item => item.name === "id");
            if (idItem?.value) {
              this.$router.push(`/aspect/${idItem.value}?update=true`);
              return;
            }
          }

          // Try direct id property
          if (data?.id) {
            this.$router.push(`/aspect/${data.id}?update=true`);
            return;
          }

          this.$emit("error", "ID not found in response");
        } catch (error) {
          console.error("Network error:", error);
          this.$emit("error", error);
        } finally {
          this.isSubmitting = false;
        }
      },
      
      // ...
    },
  
    watch: {
      aspect_form_id(newId) {
        if (!newId) return;
  
        FormDataService.get(newId).then(res => {
          // Load form data from your API
          const formData = res?.data || res;
          this.form = formData?.json || formData; // Handle both wrapped and unwrapped responses
  
          // Reset aspect with known fields
          this.aspect = {
            aspect_form_id: newId,
            date_new: '',
            date_purchased: '',
            description: '',
            opt_color: '',
            opt_star: '',
            opt_status: '',
            opt_flag: '',
          };
  
          // Initialize dynamic attributes so they're reactive
          if (this.form?.attributes) {
            this.form.attributes.forEach(attribute => {
              const attrKey = `attribute${attribute.column}`;
              this.$set(this.aspect, attrKey, "");
            });
          }
  
          // If needed for your date pickers
          this.date_new = this.form.settings.date_new || "";
          this.date_purchased = this.form.settings.date_purchased || "";

                if(this.$route.query.ref_id) {
        this.aspect.ref_id = this.$route.query.ref_id
      } 
        });
      },
    },
  };
  </script>
  
  <style scoped>
  .is-invalid {
    border-color: red;
  }
  </style>
  